import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings as SettingsIcon, Shield, Coins, BadgeCheck, Gift, Zap, Wallet, Loader2, Check, KeyRound, ChevronRight } from 'lucide-react';
import { getPaymentProfile, savePaymentProfile, SellerPaymentProfile } from '../services/dashboardSaleService';
import { supabase } from '../services/supabaseClient';
import { UserRole } from '../types';
import { fetchReputation, Reputation } from '../services/reputationService';
import { fetchCredits, CreditSummary } from '../services/creditsService';

interface SettingsViewProps {
  userRole: UserRole | null;
}

const SettingsView: React.FC<SettingsViewProps> = ({ userRole }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [reputation, setReputation] = useState<Reputation | null>(null);
  const [credits, setCredits] = useState<CreditSummary | null>(null);

  // Payout details (for selling dashboard access)
  const [payout, setPayout] = useState<SellerPaymentProfile>({});
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutSaved, setPayoutSaved] = useState(false);
  const isSeller = userRole === UserRole.RESEARCHER || userRole === UserRole.CTO;

  const savePayout = async () => {
    setPayoutSaving(true); setPayoutSaved(false);
    try { await savePaymentProfile(payout); setPayoutSaved(true); setTimeout(() => setPayoutSaved(false), 2500); }
    catch (e) { console.error(e); alert('Could not save payout details.'); }
    finally { setPayoutSaving(false); }
  };

  const loadReputation = async () => {
    try {
      const [rep, cr] = await Promise.all([fetchReputation(), fetchCredits()]);
      setReputation(rep);
      setCredits(cr);
    } catch (e) {
      console.error('Failed to load reputation/credits', e);
    }
  };

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) setEmail(user.email);
    };
    getUser();
    loadReputation();
    if (isSeller) getPaymentProfile().then((p) => p && setPayout(p)).catch(() => {});
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const getRoleDisplayName = (role: UserRole | null) => {
    switch (role) {
      case UserRole.CTO: return 'Super Admin';
      case UserRole.DATA_AGENT: return 'Data Agent';
      case UserRole.RESEARCHER: return 'Researcher';
      case UserRole.GENERAL_USER: return 'Contributor';
      case UserRole.AI_AGENT: return 'AI Agent';
      default: return role || 'Unknown';
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-4">
        <h1 className="text-xl font-bold text-white mb-1 flex items-center gap-3">
          <SettingsIcon className="text-indigo-500" />
          Settings
        </h1>
        <p className="text-slate-400 text-sm">Manage your profile and session.</p>
      </div>

      {/* Profile banner */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center text-indigo-400 border border-slate-600 shadow-inner shrink-0">
            <User size={32} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white truncate" title={email}>{email || 'Loading...'}</h3>
            <div className="flex items-center gap-1.5 mt-1">
              <Shield size={14} className="text-emerald-400" />
              <span className="text-xs text-emerald-400 font-mono uppercase tracking-wider bg-emerald-900/20 px-2 py-0.5 rounded border border-emerald-900/30">
                {getRoleDisplayName(userRole)}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 px-6 py-3 rounded-lg transition-all font-medium text-sm hover:border-red-500/40 shrink-0"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </div>

      {/* Credits + Reputation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {/* Credits: spendable balance + free monthly quota */}
      {credits && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
          <div className="p-5 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-bold flex items-center gap-2">
              <Zap size={18} className="text-indigo-400" /> Credits
            </h3>
            <div className="text-right">
              <p className="text-2xl font-bold text-white leading-none">{credits.total}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">spendable</p>
            </div>
          </div>
          <div className="p-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Free this month</span><span className="text-emerald-300 font-mono">{credits.freeRemaining}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Purchased balance</span><span className="text-slate-200 font-mono">{credits.balance}</span></div>
            <p className="text-[11px] text-slate-500 pt-1">
              Credits pay for AI analysis (≈50 per insight). A free monthly allowance is used first.
              Top-ups via KHQR/card are coming.
            </p>
          </div>
        </div>
      )}

      {/* Reputation: score + reward inventory */}
      {reputation && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
          <div className="p-5 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-bold flex items-center gap-2">
              <BadgeCheck size={18} className="text-emerald-400" /> Reputation
            </h3>
            <div className="text-right">
              <p className="text-2xl font-bold text-white leading-none">{reputation.score}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">points</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <p className="text-[11px] text-slate-500">
              Earn reputation by contributing and by consenting to share verified attributes.
              Rewards you've earned appear in your inventory below.
            </p>

            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
                <Gift size={12} className="text-indigo-400" /> Reward inventory
              </p>
              {reputation.inventory.length === 0 ? (
                <p className="text-xs text-slate-500 italic">No rewards yet.</p>
              ) : (
                reputation.inventory.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-xs">
                    <span className="text-slate-300 truncate pr-2 flex items-center gap-1.5">
                      <Coins size={12} className="text-yellow-400" /> {item.item_type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-emerald-400 font-mono">+{item.amount}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Payout details — where dashboard-sale payments land */}
      {isSeller && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
          <div className="p-5 border-b border-slate-700">
            <h3 className="text-white font-bold flex items-center gap-2"><Wallet size={18} className="text-emerald-400" /> Payout details</h3>
            <p className="text-[11px] text-slate-500 mt-1">Where buyers' payments for your dashboards are sent. Money goes directly to you.</p>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">KHQR — Bakong account ID</label>
              <input value={payout.khqr_account_id || ''} onChange={(e) => setPayout({ ...payout, khqr_account_id: e.target.value })}
                placeholder="name@bank" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">KHQR — Merchant / display name</label>
              <input value={payout.khqr_merchant_name || ''} onChange={(e) => setPayout({ ...payout, khqr_merchant_name: e.target.value })}
                placeholder="Your name" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">KHQR — City</label>
              <input value={payout.khqr_city || ''} onChange={(e) => setPayout({ ...payout, khqr_city: e.target.value })}
                placeholder="Phnom Penh" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">x402 — Wallet address (Base, USDC)</label>
              <input value={payout.x402_wallet || ''} onChange={(e) => setPayout({ ...payout, x402_wallet: e.target.value })}
                placeholder="0x…" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-indigo-500" />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button onClick={savePayout} disabled={payoutSaving}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {payoutSaving ? <Loader2 size={15} className="animate-spin" /> : payoutSaved ? <Check size={15} /> : null}
                {payoutSaved ? 'Saved' : 'Save payout details'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Developer — API keys for the agent API / MCP */}
      <button onClick={() => navigate('/settings/api')}
        className="w-full bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-5 flex items-center justify-between hover:border-indigo-500/50 transition-colors text-left">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-indigo-400"><KeyRound size={18} /></div>
          <div>
            <h3 className="text-white font-bold">API keys</h3>
            <p className="text-xs text-slate-500">Create keys for the agent API &amp; MCP server</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-slate-500" />
      </button>

      <div className="text-center space-y-2">
        <p className="text-xs text-slate-600">
            Tinaney Platform v0.1.0 (Beta)
        </p>
      </div>
    </div>
  );
};

export default SettingsView;